import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Save,
  Globe,
  MapPin,
  Building,
  Phone,
  Mail,
  FileText,
  Upload,
  Trash2,
  Image,
  Server,
  Calendar,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  ShieldCheck,
  Bell,
  BellRing,
  QrCode,
  Monitor,
  Camera,
  ExternalLink,
  Fingerprint,
  MessageCircle,
  Clock
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { isAdmin, isSuperadmin, currentOrganization } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const fileInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  
  // Default settings state
  const defaultSettings = {
    company_name: 'Mi Empresa',
    company_cif: '',
    company_address: '',
    company_city: '',
    company_postal_code: '',
    company_country: 'España',
    company_phone: '',
    company_email: '',
    company_logo: null,
    require_geolocation: true,
    geolocation_validation: true,
    default_language: 'es',
    week_starts_monday: true,
    // Overtime calculation method for the daily report/export, the monthly
    // summary screen and the payroll export templates (A3, Sage, Nominaplus...)
    overtime_gross_mode: false,
    timezone: 'Europe/Madrid',
    // App Branding
    app_name: 'Regulisto',
    app_url: '',
    page_title: 'Regulisto - Control Horario',
    favicon: null,
    // SMTP Settings
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Regulisto',
    smtp_use_tls: true,
    // Vacation Settings
    default_vacation_days: 22,
    vacation_days_by_department: null,
    holidays_add_vacation_days: false,
    // Compliance Settings
    compliance_threshold: 80,
    compliance_alert_enabled: false,
    // Missed clock-in alert (worker + supervisors, in-app and by email)
    missed_clockin_alerts_enabled: true,
    // Departments
    departments: [],
    // Absence types
    absence_types: [],
    // QR clock-in
    qr_enabled: false,
    qr_mode: 'display',
    qr_period_seconds: 30,
    // Kiosk (PIN) clock-in
    kiosk_enabled: true
  };
  
  const [settings, setSettings] = useState(defaultSettings);
  const [timezones, setTimezones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [migratingDepts, setMigratingDepts] = useState(false);
  const [newDepartment, setNewDepartment] = useState('');
  const [newAbsenceType, setNewAbsenceType] = useState({ name: '', is_justified: true, allows_hours: false });
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [qrOrgId, setQrOrgId] = useState(null);
  const [qrLocations, setQrLocations] = useState([]);

  // Reset settings when organization changes
  useEffect(() => {
    setSettings(defaultSettings);
    setLoading(true);
  }, [currentOrganization?.id]);

  useEffect(() => {
    fetchSettings();
    fetchTimezones();
    fetchQrData();
  }, [currentOrganization?.id]);

  const fetchQrData = async () => {
    try {
      const [cfgRes, locRes] = await Promise.all([
        axios.get(`${API_URL}/qr/config`),
        axios.get(`${API_URL}/locations`),
      ]);
      setQrOrgId(cfgRes.data?.organization_id || null);
      setQrLocations((locRes.data || []).filter((l) => l.is_active !== false));
    } catch (error) {
      console.error('Error fetching QR data:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimezones = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/timezones`);
      setTimezones(response.data);
    } catch (error) {
      console.error('Error fetching timezones:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings`, settings);
      toast.success(language === 'es' ? 'Configuración guardada' : 'Settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!settings.smtp_host || !settings.smtp_user || !settings.smtp_password) {
      toast.error(language === 'es' ? 'Complete la configuración SMTP antes de probar' : 'Complete SMTP configuration before testing');
      return;
    }
    
    setTestingSmtp(true);
    try {
      // First save settings
      await axios.put(`${API_URL}/settings`, settings);
      // Then test
      const response = await axios.post(`${API_URL}/settings/test-smtp`);
      toast.success(response.data.message || (language === 'es' ? 'Conexión SMTP exitosa' : 'SMTP connection successful'));
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error de conexión SMTP' : 'SMTP connection error'));
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!settings.smtp_host || !settings.smtp_user || !settings.smtp_password) {
      toast.error(language === 'es' ? 'Complete la configuración SMTP primero' : 'Complete SMTP configuration first');
      return;
    }
    
    setSendingTestEmail(true);
    try {
      // First save settings
      await axios.put(`${API_URL}/settings`, settings);
      // Then send test email
      const response = await axios.post(`${API_URL}/settings/send-test-email`);
      toast.success(response.data.message || (language === 'es' ? 'Email de prueba enviado' : 'Test email sent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al enviar email de prueba' : 'Error sending test email'));
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'es' ? 'Solo se permiten archivos PNG, JPG o SVG' : 'Only PNG, JPG, or SVG files are allowed');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(language === 'es' ? 'El archivo debe ser menor a 2MB' : 'File size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/settings/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSettings(prev => ({ ...prev, company_logo: response.data.logo }));
      toast.success(language === 'es' ? 'Logo subido correctamente' : 'Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm(language === 'es' ? '¿Eliminar el logo?' : 'Delete the logo?')) return;

    try {
      await axios.delete(`${API_URL}/settings/logo`);
      setSettings(prev => ({ ...prev, company_logo: null }));
      toast.success(language === 'es' ? 'Logo eliminado' : 'Logo deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/x-icon', 'image/vnd.microsoft.icon', 'image/svg+xml'];
    const allowedExts = ['.ico', '.png', '.svg'];
    const hasValidExt = allowedExts.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!allowedTypes.includes(file.type) && !hasValidExt) {
      toast.error(language === 'es' ? 'Solo se permiten archivos ICO, PNG o SVG' : 'Only ICO, PNG, or SVG files are allowed');
      return;
    }

    // Validate file size (500KB)
    if (file.size > 500 * 1024) {
      toast.error(language === 'es' ? 'El archivo debe ser menor a 500KB' : 'File size must be less than 500KB');
      return;
    }

    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/settings/favicon`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSettings(prev => ({ ...prev, favicon: response.data.favicon }));
      toast.success(language === 'es' ? 'Favicon subido correctamente' : 'Favicon uploaded successfully');
      
      // Update favicon in browser
      updateBrowserFavicon(response.data.favicon);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleDeleteFavicon = async () => {
    if (!window.confirm(language === 'es' ? '¿Eliminar el favicon?' : 'Delete the favicon?')) return;

    try {
      await axios.delete(`${API_URL}/settings/favicon`);
      setSettings(prev => ({ ...prev, favicon: null }));
      toast.success(language === 'es' ? 'Favicon eliminado' : 'Favicon deleted');
      
      // Reset to default favicon
      updateBrowserFavicon(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const updateBrowserFavicon = (faviconUrl) => {
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl || '/favicon.ico';
  };

  const handleMigrateDepartments = async () => {
    setMigratingDepts(true);
    try {
      const response = await axios.post(`${API_URL}/settings/migrate-departments`);
      setSettings(prev => ({ ...prev, departments: response.data.departments || [] }));
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error migrating departments');
    } finally {
      setMigratingDepts(false);
    }
  };

  const handleAddDepartment = () => {
    if (!newDepartment.trim()) return;
    const currentDepts = settings.departments || [];
    if (currentDepts.some(d => d.toLowerCase() === newDepartment.trim().toLowerCase())) {
      toast.error(language === 'es' ? 'El departamento ya existe' : 'Department already exists');
      return;
    }
    setSettings(prev => ({
      ...prev,
      departments: [...currentDepts, newDepartment.trim()].sort()
    }));
    setNewDepartment('');
  };

  const handleRemoveDepartment = (dept) => {
    setSettings(prev => ({
      ...prev,
      departments: (prev.departments || []).filter(d => d !== dept)
    }));
  };

  const handleAddAbsenceType = () => {
    if (!newAbsenceType.name.trim()) return;
    const currentTypes = settings.absence_types || [];
    if (currentTypes.some(t => t.name.toLowerCase() === newAbsenceType.name.trim().toLowerCase())) {
      toast.error(language === 'es' ? 'El tipo de ausencia ya existe' : 'Absence type already exists');
      return;
    }
    const newType = {
      id: `custom_${Date.now()}`,
      name: newAbsenceType.name.trim(),
      is_justified: newAbsenceType.is_justified,
      allows_hours: newAbsenceType.allows_hours
    };
    setSettings(prev => ({
      ...prev,
      absence_types: [...currentTypes, newType]
    }));
    setNewAbsenceType({ name: '', is_justified: true, allows_hours: false });
  };

  const handleRemoveAbsenceType = (typeId) => {
    setSettings(prev => ({
      ...prev,
      absence_types: (prev.absence_types || []).filter(t => t.id !== typeId)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">{t('settings')}</h1>
        <p className="text-slate-500">
          {language === 'es' ? 'Configuración general del sistema' : 'General system settings'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Info */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Building className="w-5 h-5 text-indigo-600" />
              {language === 'es' ? 'Datos de la Empresa' : 'Company Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Logo Upload Section */}
            <div className="flex items-start gap-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex-shrink-0">
                {settings.company_logo ? (
                  <div className="relative">
                    <img 
                      src={settings.company_logo} 
                      alt="Company Logo" 
                      className="w-24 h-24 object-contain rounded-lg border border-slate-200 bg-white p-2"
                    />
                    <button
                      onClick={handleDeleteLogo}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      title={language === 'es' ? 'Eliminar logo' : 'Delete logo'}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 flex items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-white">
                    <Image className="w-8 h-8 text-slate-400" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <Label className="mb-2 block">{language === 'es' ? 'Logo de la empresa' : 'Company logo'}</Label>
                <p className="text-sm text-slate-500 mb-3">
                  {language === 'es' 
                    ? 'Sube el logo de tu empresa. Formatos: PNG, JPG, SVG. Máximo 2MB.'
                    : 'Upload your company logo. Formats: PNG, JPG, SVG. Max 2MB.'}
                </p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner" />
                      {language === 'es' ? 'Subiendo...' : 'Uploading...'}
                    </span>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {language === 'es' ? 'Subir Logo' : 'Upload Logo'}
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Nombre de la empresa' : 'Company name'}</Label>
                <Input
                  value={settings.company_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>CIF/NIF</Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={settings.company_cif}
                    onChange={(e) => setSettings(prev => ({ ...prev, company_cif: e.target.value }))}
                    className="pl-10"
                    placeholder="B12345678"
                    data-testid="company-cif-input"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Dirección' : 'Address'}</Label>
              <Input
                value={settings.company_address}
                onChange={(e) => setSettings(prev => ({ ...prev, company_address: e.target.value }))}
                placeholder={language === 'es' ? 'Calle, número, piso...' : 'Street, number, floor...'}
                data-testid="company-address-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Ciudad' : 'City'}</Label>
                <Input
                  value={settings.company_city}
                  onChange={(e) => setSettings(prev => ({ ...prev, company_city: e.target.value }))}
                  data-testid="company-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Código Postal' : 'Postal Code'}</Label>
                <Input
                  value={settings.company_postal_code}
                  onChange={(e) => setSettings(prev => ({ ...prev, company_postal_code: e.target.value }))}
                  data-testid="company-postal-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'es' ? 'País' : 'Country'}</Label>
                <Input
                  value={settings.company_country}
                  onChange={(e) => setSettings(prev => ({ ...prev, company_country: e.target.value }))}
                  data-testid="company-country-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={settings.company_phone}
                    onChange={(e) => setSettings(prev => ({ ...prev, company_phone: e.target.value }))}
                    className="pl-10"
                    placeholder="+34 900 000 000"
                    data-testid="company-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Email de contacto' : 'Contact email'}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={settings.company_email}
                    onChange={(e) => setSettings(prev => ({ ...prev, company_email: e.target.value }))}
                    className="pl-10"
                    placeholder="info@empresa.com"
                    data-testid="company-email-input"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* App Branding - SUPERADMIN ONLY */}
        {isSuperadmin() && (
          <Card className="border-slate-200 lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                {language === 'es' ? 'Personalización de la Aplicación' : 'App Customization'}
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full ml-2">
                  {language === 'es' ? 'Solo Superadmin' : 'Superadmin only'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Nombre de la aplicación' : 'Application name'}</Label>
                  <Input
                    value={settings.app_name}
                    onChange={(e) => setSettings(prev => ({ ...prev, app_name: e.target.value }))}
                    placeholder="Regulisto"
                    data-testid="app-name-input"
                  />
                  <p className="text-xs text-slate-500">
                    {language === 'es' 
                      ? 'Este nombre aparecerá en los emails y notificaciones'
                      : 'This name will appear in emails and notifications'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'URL de la aplicación' : 'Application URL'}</Label>
                  <Input
                    value={settings.app_url}
                    onChange={(e) => setSettings(prev => ({ ...prev, app_url: e.target.value }))}
                    placeholder="https://miempresa.ejemplo.com"
                    data-testid="app-url-input"
                  />
                  <p className="text-xs text-slate-500">
                    {language === 'es' 
                      ? 'URL que se incluirá en los emails de credenciales'
                      : 'URL to include in credential emails'}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Título de la página' : 'Page title'}</Label>
                  <Input
                    value={settings.page_title}
                    onChange={(e) => {
                      setSettings(prev => ({ ...prev, page_title: e.target.value }));
                      document.title = e.target.value || 'Regulisto';
                    }}
                    placeholder="Regulisto - Control Horario"
                    data-testid="page-title-input"
                  />
                  <p className="text-xs text-slate-500">
                    {language === 'es' 
                      ? 'Título que aparece en las pestañas del navegador'
                      : 'Title shown in browser tabs'}
                  </p>
                </div>
                
                {/* Favicon Upload */}
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Favicon' : 'Favicon'}</Label>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                      {settings.favicon ? (
                        <img src={settings.favicon} alt="Favicon" className="w-8 h-8 object-contain" />
                      ) : (
                        <Globe className="w-6 h-6 text-slate-300" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <input
                        ref={faviconInputRef}
                        type="file"
                        accept=".ico,.png,.svg,image/x-icon,image/png,image/svg+xml"
                        onChange={handleFaviconUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => faviconInputRef.current?.click()}
                        disabled={uploadingFavicon}
                      >
                        {uploadingFavicon ? (
                          <span className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                            {language === 'es' ? 'Subiendo...' : 'Uploading...'}
                          </span>
                        ) : (
                          language === 'es' ? 'Subir favicon' : 'Upload favicon'
                        )}
                      </Button>
                      {settings.favicon && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleDeleteFavicon}
                          className="text-red-500 hover:text-red-700"
                        >
                          {language === 'es' ? 'Eliminar' : 'Delete'}
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    {language === 'es' 
                      ? 'Icono que aparece en las pestañas (ICO, PNG o SVG, máx. 500KB)'
                      : 'Icon shown in browser tabs (ICO, PNG or SVG, max 500KB)'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Calendar Settings */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              {language === 'es' ? 'Calendario' : 'Calendar'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Inicio de semana' : 'Week starts on'}</Label>
              <Select 
                value={settings.week_starts_monday ? 'monday' : 'sunday'} 
                onValueChange={(value) => {
                  setSettings(prev => ({ ...prev, week_starts_monday: value === 'monday' }));
                }}
              >
                <SelectTrigger data-testid="week-start-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monday">{language === 'es' ? 'Lunes' : 'Monday'}</SelectItem>
                  <SelectItem value="sunday">{language === 'es' ? 'Domingo' : 'Sunday'}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {language === 'es' 
                  ? 'Define el primer día de la semana en los calendarios'
                  : 'Define the first day of the week in calendars'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Language Settings */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Globe className="w-5 h-5 text-indigo-600" />
              {language === 'es' ? 'Idioma' : 'Language'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Idioma por defecto' : 'Default language'}</Label>
              <Select 
                value={settings.default_language} 
                onValueChange={(value) => {
                  setSettings(prev => ({ ...prev, default_language: value }));
                  setLanguage(value);
                }}
              >
                <SelectTrigger data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Zona horaria' : 'Timezone'}</Label>
              <p className="text-sm text-slate-500 mb-2">
                {language === 'es' 
                  ? 'Las horas de los fichajes se mostrarán en esta zona horaria en los informes exportados'
                  : 'Time records will be displayed in this timezone in exported reports'}
              </p>
              <Select 
                value={settings.timezone || 'Europe/Madrid'} 
                onValueChange={(value) => setSettings(prev => ({ ...prev, timezone: value }))}
              >
                <SelectTrigger data-testid="timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timezones.map(tz => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Overtime Calculation Method */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-600" />
              {language === 'es' ? 'Cálculo de horas extra' : 'Overtime calculation'}
            </CardTitle>
            <p className="text-sm text-slate-500">
              {language === 'es'
                ? 'Este ajuste se aplica por igual al informe/exportación diaria, al resumen mensual y a las plantillas de exportación de nómina (A3, Sage, Nominaplus...).'
                : 'This setting applies consistently to the daily report/export, the monthly summary and the payroll export templates (A3, Sage, Nominaplus...).'}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="pr-4">
                <Label>{language === 'es' ? 'Usar horas brutas' : 'Use gross hours'}</Label>
                <p className="text-sm text-slate-500">
                  {language === 'es'
                    ? 'Desactivado (recomendado): balance neto del periodo — las horas trabajadas de más compensan las de menos. Activado: se suman solo los excesos diarios positivos, sin compensar los días con déficit.'
                    : 'Off (recommended): net balance for the period — hours worked over compensate hours worked under. On: only each day\'s positive excess is added up, without offsetting deficit days.'}
                </p>
              </div>
              <Switch
                checked={settings.overtime_gross_mode}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, overtime_gross_mode: checked }))}
                data-testid="overtime-gross-mode-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Geolocation Settings */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-indigo-600" />
              {language === 'es' ? 'Geolocalización' : 'Geolocation'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label>{language === 'es' ? 'Requerir geolocalización' : 'Require geolocation'}</Label>
                <p className="text-sm text-slate-500">
                  {language === 'es' 
                    ? 'Los empleados deben proporcionar su ubicación al fichar'
                    : 'Employees must provide their location when clocking'}
                </p>
              </div>
              <Switch
                checked={settings.require_geolocation}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, require_geolocation: checked }))}
                data-testid="require-geolocation-switch"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>{language === 'es' ? 'Validar ubicación' : 'Validate location'}</Label>
                <p className="text-sm text-slate-500">
                  {language === 'es' 
                    ? 'Verificar que el empleado esté dentro del radio permitido'
                    : 'Verify employee is within allowed radius'}
                </p>
              </div>
              <Switch
                checked={settings.geolocation_validation}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, geolocation_validation: checked }))}
                data-testid="validate-location-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Clock-in Methods */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Fingerprint className="w-5 h-5 text-indigo-600" />
              {language === 'es' ? 'Métodos de fichaje' : 'Clock-in methods'}
            </CardTitle>
            <p className="text-sm text-slate-500">
              {language === 'es'
                ? 'Elige qué métodos pueden usar los trabajadores de tu empresa para fichar'
                : 'Choose which methods your company\'s workers can use to clock in'}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Fingerprint className="w-5 h-5 text-slate-500" />
                <div>
                  <Label>{language === 'es' ? 'Kiosko (PIN)' : 'Kiosk (PIN)'}</Label>
                  <p className="text-sm text-slate-500">
                    {language === 'es'
                      ? 'Dispositivo compartido donde el trabajador introduce su PIN'
                      : 'Shared device where the worker enters their PIN'}
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.kiosk_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, kiosk_enabled: checked }))}
                data-testid="kiosk-enabled-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div className="flex items-center gap-3">
                <QrCode className="w-5 h-5 text-slate-500" />
                <div>
                  <Label>{language === 'es' ? 'Código QR' : 'QR code'}</Label>
                  <p className="text-sm text-slate-500">
                    {language === 'es'
                      ? 'Fichaje mediante códigos QR dinámicos, configurable más abajo'
                      : 'Clock-in via dynamic QR codes, configurable below'}
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.qr_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, qr_enabled: checked }))}
                data-testid="qr-enabled-switch-methods"
              />
            </div>

            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg bg-slate-50 opacity-75">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-slate-400" />
                <div>
                  <div className="flex items-center gap-2">
                    <Label className="text-slate-500">WhatsApp</Label>
                    <Badge variant="secondary" className="bg-slate-200 text-slate-600 text-xs">
                      {language === 'es' ? 'Próximamente' : 'Coming soon'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-400">
                    {language === 'es'
                      ? 'Fichar enviando un mensaje de WhatsApp'
                      : 'Clock in by sending a WhatsApp message'}
                  </p>
                </div>
              </div>
              <Switch checked={false} disabled data-testid="whatsapp-enabled-switch" />
            </div>
          </CardContent>
        </Card>

        {/* QR Clock-in Configuration */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <QrCode className="w-5 h-5 text-indigo-600" />
              {language === 'es' ? 'Fichaje por código QR' : 'QR code clock-in'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label>{language === 'es' ? 'Activar fichaje por QR' : 'Enable QR clock-in'}</Label>
                <p className="text-sm text-slate-500">
                  {language === 'es'
                    ? 'Permite fichar mediante códigos QR dinámicos con validación de ubicación'
                    : 'Allow clocking via dynamic QR codes with location validation'}
                </p>
              </div>
              <Switch
                checked={settings.qr_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, qr_enabled: checked }))}
                data-testid="qr-enabled-switch"
              />
            </div>

            {settings.qr_enabled && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Modo de funcionamiento' : 'Operation mode'}</Label>
                    <Select
                      value={settings.qr_mode}
                      onValueChange={(value) => setSettings(prev => ({ ...prev, qr_mode: value }))}
                    >
                      <SelectTrigger data-testid="qr-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="display">
                          {language === 'es' ? 'Pantalla muestra el QR (el trabajador escanea)' : 'Screen shows QR (worker scans)'}
                        </SelectItem>
                        <SelectItem value="scanner">
                          {language === 'es' ? 'Lector escanea al trabajador' : 'Reader scans worker'}
                        </SelectItem>
                        <SelectItem value="both">
                          {language === 'es' ? 'Ambos modos' : 'Both modes'}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Caducidad del código (segundos)' : 'Code expiry (seconds)'}</Label>
                    <Input
                      type="number"
                      min={10}
                      max={120}
                      value={settings.qr_period_seconds}
                      onChange={(e) => setSettings(prev => ({ ...prev, qr_period_seconds: parseInt(e.target.value) || 30 }))}
                      data-testid="qr-period-input"
                    />
                    <p className="text-xs text-slate-400">
                      {language === 'es' ? 'Recomendado: 30 segundos. Entre 10 y 120.' : 'Recommended: 30 seconds. Between 10 and 120.'}
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    {language === 'es'
                      ? 'Guarda los cambios antes de abrir las pantallas. Cada ubicación tiene su propio enlace.'
                      : 'Save changes before opening the screens. Each location has its own link.'}
                  </p>
                </div>

                <div className="space-y-3">
                  <Label>{language === 'es' ? 'Pantallas por ubicación' : 'Screens per location'}</Label>
                  {qrLocations.length === 0 && (
                    <p className="text-sm text-slate-500">
                      {language === 'es'
                        ? 'No hay ubicaciones configuradas. Crea ubicaciones para generar los enlaces.'
                        : 'No locations configured. Create locations to generate links.'}
                    </p>
                  )}
                  {qrLocations.map((loc) => {
                    const base = window.location.origin;
                    const displayUrl = `${base}/qr-display?org=${encodeURIComponent(qrOrgId || '')}&loc=${encodeURIComponent(loc.id)}`;
                    const scannerUrl = `${base}/qr-scanner?org=${encodeURIComponent(qrOrgId || '')}&loc=${encodeURIComponent(loc.id)}`;
                    return (
                      <div key={loc.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 p-3 border border-slate-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-700">{loc.name}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {['display', 'both'].includes(settings.qr_mode) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(displayUrl, '_blank')}
                            >
                              <Monitor className="w-4 h-4 mr-1" />
                              {language === 'es' ? 'Abrir pantalla QR' : 'Open QR screen'}
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </Button>
                          )}
                          {['scanner', 'both'].includes(settings.qr_mode) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(scannerUrl, '_blank')}
                            >
                              <Camera className="w-4 h-4 mr-1" />
                              {language === 'es' ? 'Abrir lector' : 'Open reader'}
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* SMTP Configuration */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <Server className="w-5 h-5 text-indigo-600" />
                {language === 'es' ? 'Configuración de Email (SMTP)' : 'Email Configuration (SMTP)'}
              </CardTitle>
              {settings.smtp_host && settings.smtp_user && (
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-green-600">
                    {language === 'es' ? 'Configurado' : 'Configured'}
                  </span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                {language === 'es' 
                  ? '⚠️ Configura el servidor SMTP para enviar correos de credenciales y notificaciones a los empleados.'
                  : '⚠️ Configure SMTP server to send credential emails and notifications to employees.'}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Servidor SMTP' : 'SMTP Server'}</Label>
                <Input
                  value={settings.smtp_host}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                  placeholder="smtp.gmail.com"
                  data-testid="smtp-host-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Puerto' : 'Port'}</Label>
                <Input
                  type="number"
                  value={settings.smtp_port}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                  placeholder="587"
                  data-testid="smtp-port-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Usuario SMTP' : 'SMTP User'}</Label>
                <Input
                  value={settings.smtp_user}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
                  placeholder="tu@email.com"
                  data-testid="smtp-user-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Contraseña SMTP' : 'SMTP Password'}</Label>
                <div className="relative">
                  <Input
                    type={showSmtpPassword ? 'text' : 'password'}
                    value={settings.smtp_password}
                    onChange={(e) => setSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                    placeholder="••••••••"
                    className="pr-10"
                    data-testid="smtp-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Email remitente' : 'From Email'}</Label>
                <Input
                  type="email"
                  value={settings.smtp_from_email}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_from_email: e.target.value }))}
                  placeholder="noreply@empresa.com"
                  data-testid="smtp-from-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Nombre remitente' : 'From Name'}</Label>
                <Input
                  value={settings.smtp_from_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, smtp_from_name: e.target.value }))}
                  placeholder="Regulisto"
                  data-testid="smtp-from-name-input"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings.smtp_use_tls}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, smtp_use_tls: checked }))}
                  data-testid="smtp-tls-switch"
                />
                <Label>{language === 'es' ? 'Usar TLS' : 'Use TLS'}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTestSmtp}
                  disabled={testingSmtp || !settings.smtp_host || !settings.smtp_user || !settings.smtp_password}
                  title={(!settings.smtp_host || !settings.smtp_user || !settings.smtp_password) 
                    ? (language === 'es' ? 'Complete todos los campos SMTP para probar' : 'Complete all SMTP fields to test') 
                    : ''}
                >
                  {testingSmtp ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner" />
                      {language === 'es' ? 'Probando...' : 'Testing...'}
                    </span>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      {language === 'es' ? 'Probar conexión' : 'Test connection'}
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="default"
                  onClick={handleSendTestEmail}
                  disabled={sendingTestEmail || !settings.smtp_host || !settings.smtp_user || !settings.smtp_password}
                  data-testid="send-test-email-btn"
                >
                  {sendingTestEmail ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner" />
                      {language === 'es' ? 'Enviando...' : 'Sending...'}
                    </span>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      {language === 'es' ? 'Enviar email de prueba' : 'Send test email'}
                    </>
                  )}
                </Button>
                {(!settings.smtp_host || !settings.smtp_user || !settings.smtp_password) && (
                  <span className="text-xs text-amber-600">
                    {language === 'es' ? 'Complete todos los campos' : 'Complete all fields'}
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vacation Configuration */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              {language === 'es' ? 'Configuración de Vacaciones' : 'Vacation Configuration'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Días de vacaciones por defecto (anual)' : 'Default vacation days (annual)'}</Label>
              <Input
                type="number"
                min="0"
                max="365"
                value={settings.default_vacation_days || 22}
                onChange={(e) => setSettings(prev => ({ ...prev, default_vacation_days: parseInt(e.target.value) || 22 }))}
                className="w-32"
                data-testid="default-vacation-days-input"
              />
              <p className="text-xs text-slate-500">
                {language === 'es' 
                  ? 'Días de vacaciones por año para empleados nuevos (se calcula proporcional a la fecha de contratación)'
                  : 'Vacation days per year for new employees (proportional to hire date)'}
              </p>
            </div>
            
            <div className="space-y-3">
              <Label>{language === 'es' ? 'Días por departamento' : 'Days by department'}</Label>
              <p className="text-xs text-slate-500">
                {language === 'es' 
                  ? 'Configure días específicos por departamento. Los departamentos no listados usarán el valor por defecto.'
                  : 'Configure specific days by department. Unlisted departments use the default value.'}
              </p>
              
              {/* Department vacation days list */}
              {settings.vacation_days_by_department && Object.keys(settings.vacation_days_by_department).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(settings.vacation_days_by_department).map(([dept, days]) => (
                    <div key={dept} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                      <span className="flex-1 font-medium">{dept}</span>
                      <Input
                        type="number"
                        min="0"
                        max="365"
                        value={days}
                        onChange={(e) => {
                          const newDays = { ...settings.vacation_days_by_department };
                          newDays[dept] = parseInt(e.target.value) || 0;
                          setSettings(prev => ({ ...prev, vacation_days_by_department: newDays }));
                        }}
                        className="w-20"
                      />
                      <span className="text-sm text-slate-500">{language === 'es' ? 'días' : 'days'}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => {
                          const newDays = { ...settings.vacation_days_by_department };
                          delete newDays[dept];
                          setSettings(prev => ({ ...prev, vacation_days_by_department: Object.keys(newDays).length > 0 ? newDays : null }));
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">
                  {language === 'es' ? 'No hay configuración por departamento' : 'No department-specific configuration'}
                </p>
              )}
              
              {/* Add department */}
              <div className="flex items-center gap-2 pt-2">
                <Input
                  id="new-dept-name"
                  placeholder={language === 'es' ? 'Nombre del departamento' : 'Department name'}
                  className="flex-1"
                />
                <Input
                  id="new-dept-days"
                  type="number"
                  min="0"
                  max="365"
                  placeholder="22"
                  className="w-20"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const deptInput = document.getElementById('new-dept-name');
                    const daysInput = document.getElementById('new-dept-days');
                    const dept = deptInput.value.trim();
                    const days = parseInt(daysInput.value) || 22;
                    
                    if (dept) {
                      const newDays = { ...(settings.vacation_days_by_department || {}), [dept]: days };
                      setSettings(prev => ({ ...prev, vacation_days_by_department: newDays }));
                      deptInput.value = '';
                      daysInput.value = '';
                    }
                  }}
                >
                  {language === 'es' ? 'Añadir' : 'Add'}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="space-y-1">
                <Label className="text-sm font-medium">
                  {language === 'es' ? 'Festivos trabajados suman vacaciones' : 'Worked holidays add vacation days'}
                </Label>
                <p className="text-xs text-slate-500">
                  {language === 'es'
                    ? 'Cuando está activo, cada día festivo trabajado se añade automáticamente al saldo de vacaciones del empleado.'
                    : 'When enabled, each worked holiday is automatically added to the employee\'s vacation balance.'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={settings.holidays_add_vacation_days}
                onClick={() => setSettings(prev => ({ ...prev, holidays_add_vacation_days: !prev.holidays_add_vacation_days }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                  settings.holidays_add_vacation_days ? 'bg-indigo-600' : 'bg-slate-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    settings.holidays_add_vacation_days ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Missed Clock-in Alert */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <BellRing className="w-5 h-5 text-indigo-600" />
              {language === 'es' ? 'Aviso de fichaje no realizado' : 'Missed clock-in alert'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="pr-4">
                <Label>{language === 'es' ? 'Avisar cuando falte un fichaje' : 'Alert on a missed clock-in'}</Label>
                <p className="text-sm text-slate-500">
                  {language === 'es'
                    ? 'Si pasan 15 minutos desde el inicio de un turno programado sin fichaje de entrada, se avisa al trabajador y a su supervisor o administrador (en la app y por email).'
                    : "If 15 minutes pass after a scheduled shift's start with no clock-in, the worker and their supervisor or admin are alerted (in-app and by email)."}
                </p>
              </div>
              <Switch
                checked={settings.missed_clockin_alerts_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, missed_clockin_alerts_enabled: checked }))}
                data-testid="missed-clockin-alerts-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Compliance Threshold Configuration */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-600" />
              {language === 'es' ? 'Alerta de Cumplimiento' : 'Compliance Alert'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
              <p className="text-sm text-teal-800">
                {language === 'es' 
                  ? 'Configure una alerta que se activará cuando la puntuación de cumplimiento mensual caiga por debajo del umbral establecido.'
                  : 'Set an alert that triggers when the monthly compliance score falls below the threshold.'}
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>{language === 'es' ? 'Activar alerta de cumplimiento' : 'Enable compliance alert'}</Label>
                <p className="text-sm text-slate-500">
                  {language === 'es' 
                    ? 'Recibe alertas cuando el cumplimiento sea bajo'
                    : 'Receive alerts when compliance is low'}
                </p>
              </div>
              <Switch
                checked={settings.compliance_alert_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, compliance_alert_enabled: checked }))}
                data-testid="compliance-alert-switch"
              />
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{language === 'es' ? 'Umbral de cumplimiento' : 'Compliance threshold'}</Label>
                <span className="text-2xl font-bold text-indigo-600">{settings.compliance_threshold || 80}%</span>
              </div>
              <Input
                type="range"
                min="50"
                max="100"
                step="5"
                value={settings.compliance_threshold || 80}
                onChange={(e) => setSettings(prev => ({ ...prev, compliance_threshold: parseInt(e.target.value) }))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                data-testid="compliance-threshold-slider"
              />
              <div className="flex justify-between text-xs text-slate-500">
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>
              <p className="text-xs text-slate-500">
                {language === 'es' 
                  ? 'Si la puntuación de cumplimiento cae por debajo de este valor, se mostrará una alerta en el dashboard de tendencias.'
                  : 'If the compliance score falls below this value, an alert will be shown in the trends dashboard.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Departments Configuration */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Building className="w-5 h-5 text-orange-600" />
              {language === 'es' ? 'Departamentos' : 'Departments'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-800">
                {language === 'es' 
                  ? 'Define los departamentos de tu empresa. Los empleados podrán seleccionar de esta lista.'
                  : 'Define your company departments. Employees will be able to select from this list.'}
              </p>
            </div>
            
            {/* Migrate button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleMigrateDepartments}
              disabled={migratingDepts}
              className="w-full"
              data-testid="migrate-departments-btn"
            >
              {migratingDepts ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin" />
                  {language === 'es' ? 'Migrando...' : 'Migrating...'}
                </span>
              ) : (
                language === 'es' ? 'Importar departamentos existentes' : 'Import existing departments'
              )}
            </Button>
            
            {/* Add new department */}
            <div className="flex gap-2">
              <Input
                value={newDepartment}
                onChange={(e) => setNewDepartment(e.target.value)}
                placeholder={language === 'es' ? 'Nuevo departamento...' : 'New department...'}
                onKeyDown={(e) => e.key === 'Enter' && handleAddDepartment()}
                data-testid="new-department-input"
              />
              <Button
                onClick={handleAddDepartment}
                disabled={!newDepartment.trim()}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="add-department-btn"
              >
                {language === 'es' ? 'Añadir' : 'Add'}
              </Button>
            </div>
            
            {/* Department list */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(settings.departments || []).length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  {language === 'es' ? 'No hay departamentos configurados' : 'No departments configured'}
                </p>
              ) : (
                (settings.departments || []).map((dept, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium text-slate-700">{dept}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveDepartment(dept)}
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      ×
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Absence Types Configuration */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              {language === 'es' ? 'Tipos de Ausencia' : 'Absence Types'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-800">
                {language === 'es' 
                  ? 'Configura los tipos de ausencia disponibles. Puedes marcar si están justificadas y si permiten registrar horas.'
                  : 'Configure available absence types. You can mark if they are justified and if they allow hour tracking.'}
              </p>
            </div>
            
            {/* Add new absence type */}
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <Input
                value={newAbsenceType.name}
                onChange={(e) => setNewAbsenceType(prev => ({ ...prev, name: e.target.value }))}
                placeholder={language === 'es' ? 'Nombre del tipo de ausencia...' : 'Absence type name...'}
                data-testid="new-absence-type-input"
              />
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newAbsenceType.is_justified}
                    onChange={(e) => setNewAbsenceType(prev => ({ ...prev, is_justified: e.target.checked }))}
                    className="rounded border-slate-300"
                  />
                  {language === 'es' ? 'Justificada' : 'Justified'}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newAbsenceType.allows_hours}
                    onChange={(e) => setNewAbsenceType(prev => ({ ...prev, allows_hours: e.target.checked }))}
                    className="rounded border-slate-300"
                  />
                  {language === 'es' ? 'Permite horas' : 'Allows hours'}
                </label>
              </div>
              <Button
                onClick={handleAddAbsenceType}
                disabled={!newAbsenceType.name.trim()}
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="add-absence-type-btn"
              >
                {language === 'es' ? 'Añadir tipo de ausencia' : 'Add absence type'}
              </Button>
            </div>
            
            {/* Absence types list */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(settings.absence_types || []).length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  {language === 'es' ? 'Se usarán los tipos predeterminados' : 'Default types will be used'}
                </p>
              ) : (
                (settings.absence_types || []).map((type) => (
                  <div key={type.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <div>
                      <span className="text-sm font-medium text-slate-700">{type.name}</span>
                      <div className="flex gap-2 mt-1">
                        {type.is_justified && (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                            {language === 'es' ? 'Justificada' : 'Justified'}
                          </span>
                        )}
                        {type.allows_hours && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                            {language === 'es' ? 'Por horas' : 'Hourly'}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAbsenceType(type.id)}
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      ×
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      {isAdmin() && (
        <div className="flex justify-end">
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="spinner" />
                {language === 'es' ? 'Guardando...' : 'Saving...'}
              </span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {t('save')}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Settings;
